import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './FeedCard.css';

/**
 * Get navigation hint text for a feed item.
 */
const getNavigationHint = (item, isHebrew) => {
  if (item.externalUrl) {
    return isHebrew ? '\u05E4\u05EA\u05D7 \u05E7\u05D9\u05E9\u05D5\u05E8 \u2190' : 'Open link \u2192';
  }
  switch (item.source) {
    case 'ai_alert':
      return isHebrew ? '\u05E2\u05D1\u05D5\u05E8 \u05DC-AI Hub \u2190' : 'Go to AI Hub \u2192';
    case 'practice_activity':
      switch (item.category) {
        case 'appointment':
          return isHebrew ? '\u05E2\u05D1\u05D5\u05E8 \u05DC\u05DC\u05D5\u05D7 \u05E9\u05E0\u05D4 \u2190' : 'Go to Calendar \u2192';
        case 'patient':
          return isHebrew ? '\u05E2\u05D1\u05D5\u05E8 \u05DC\u05DE\u05D8\u05D5\u05E4\u05DC\u05D9\u05DD \u2190' : 'Go to Patients \u2192';
        case 'billing':
          return isHebrew ? '\u05E2\u05D1\u05D5\u05E8 \u05DC\u05D7\u05D9\u05D5\u05D1 \u2190' : 'Go to Billing \u2192';
        default:
          return null;
      }
    case 'clinical_win':
      return isHebrew ? '\u05E2\u05D1\u05D5\u05E8 \u05DC\u05D3\u05D5\u05D7\u05D5\u05EA \u2190' : 'Go to Reports \u2192';
    default:
      return null;
  }
};

/**
 * Get navigation target icon for a feed item.
 */
const getNavigationIcon = (item) => {
  if (item.externalUrl) return '\uD83D\uDD17';
  switch (item.source) {
    case 'ai_alert': return '\uD83E\uDD16';
    case 'practice_activity':
      switch (item.category) {
        case 'appointment': return '\uD83D\uDCC5';
        case 'patient': return '\uD83D\uDC64';
        case 'billing': return '\uD83D\uDCB3';
        default: return '\u27A1\uFE0F';
      }
    case 'clinical_win': return '\uD83D\uDCCA';
    default: return null;
  }
};

/**
 * FeedCard — glassmorphic card for individual feed items.
 * RTL-aware with Hebrew support and navigation hints.
 */
// Helper for the unanswered_question card: format ISO date as a short
// time like "10:35" or "אתמול" if isHebrew (best-effort, no library).
const _formatTimeShort = (iso, isHebrew) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(isHebrew ? 'he-IL' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return '';
  }
};

export const FeedCard = ({
  item,
  mode = 'full',
  language = 'he',
  onClick,
  onLike,
  onBookmark,
  onDismiss,
  isNew = false,
}) => {
  const isHebrew = language === 'he';
  const { authFetch } = useAuth();

  // Hooks must run unconditionally (rules of hooks) — declare answer state
  // up front even though it's only consumed by the unanswered_question
  // branch below. Other card variants ignore them.
  const [answerText, setAnswerText] = useState('');
  const [answered, setAnswered] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Action-Needed: unanswered_question card ─────────────────────────
  // Backend FeedItem with action_type='unanswered_question' carries the
  // raw question in subtitle and the source UnansweredQuestion.id in
  // item_metadata so the answer_and_add_to_kb action can resolve it.
  const itemActionType = item.actionType || item.action_type;
  if (itemActionType === 'unanswered_question') {
    const meta = item.metadata || item.item_metadata || {};
    const questionText = item.subtitle || meta.question || '';
    const createdAt = item.createdAt || item.created_at;

    const handleSaveAnswer = async () => {
      const answer = answerText.trim();
      if (!answer || saving) return;
      setSaving(true);
      try {
        const res = await authFetch('/api/feed/actions/execute', {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify({
            item_id: item.id,
            action_type: 'answer_and_add_to_kb',
            metadata: {
              answer,
              add_to_kb: true,
              unanswered_question_id: meta.unanswered_question_id,
            },
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.success === false) throw new Error(data.message || 'failed');
        setAnswered(true);
      } catch (e) {
        // Surface a minimal inline error; the toast system isn't wired
        // into FeedCard's call site here.
        setAnswered(false);
        alert(isHebrew ? `שגיאה: ${e.message}` : `Error: ${e.message}`);
      } finally {
        setSaving(false);
      }
    };

    // Real dismissal — marks the QUESTION dismissed in the backend
    // ("not something the system should answer"), unlike the local
    // Cancel button which only collapses the input.
    const handleDismissQuestion = async () => {
      if (saving) return;
      setSaving(true);
      try {
        const res = await authFetch('/api/feed/actions/execute', {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify({
            item_id: item.id,
            action_type: 'dismiss_question',
            metadata: {
              unanswered_question_id: meta.unanswered_question_id,
            },
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.success === false) throw new Error(data.message || 'failed');
        setDismissed(true);
      } catch (e) {
        alert(isHebrew ? `שגיאה: ${e.message}` : `Error: ${e.message}`);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="feed-card feed-card--warning">
        <div className="feed-card__header">
          <span className="feed-card__icon">❓</span>
          <span className="feed-card__title">{item.title}</span>
          <span className="feed-card__time">
            {_formatTimeShort(createdAt, isHebrew)}
          </span>
        </div>
        <div className="feed-card__body">
          <p className="feed-card__question">"{questionText}"</p>
          {dismissed ? (
            <p style={{ color: '#888', marginTop: '8px' }}>
              {isHebrew
                ? '🚫 סומנה כלא רלוונטית — לא תישמר למאגר'
                : '🚫 Dismissed — will not be saved to the KB'}
            </p>
          ) : !answered ? (
            <>
              <textarea
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder={isHebrew ? 'כתוב תשובה כאן...' : 'Write the answer…'}
                rows={3}
                disabled={saving}
                style={{
                  width: '100%',
                  marginTop: '8px',
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  direction: isHebrew ? 'rtl' : 'ltr',
                  fontFamily: 'inherit',
                }}
              />
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleSaveAnswer}
                  disabled={!answerText.trim() || saving}
                  style={{
                    background: '#00f5ff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  {saving ? '...' : (isHebrew ? '✅ שמור והוסף ל-KB' : '✅ Save & add to KB')}
                </button>
                <button
                  onClick={handleDismissQuestion}
                  disabled={saving}
                  title={isHebrew
                    ? 'השאלה אינה רלוונטית למערכת — נסגרת ולא נשמרת'
                    : 'Not a question the system should answer'}
                  style={{
                    background: '#fde8e8',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                  }}
                >
                  {isHebrew ? '🚫 לא רלוונטי' : '🚫 Not relevant'}
                </button>
                <button
                  onClick={() => onDismiss && onDismiss(item.id)}
                  disabled={saving}
                  style={{
                    background: '#eee',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                  }}
                >
                  {isHebrew ? 'בטל' : 'Cancel'}
                </button>
              </div>
            </>
          ) : (
            <p style={{ color: 'green', marginTop: '8px' }}>
              {isHebrew
                ? '✅ התשובה נשמרה ונוספה למאגר הידע'
                : '✅ Answer saved and added to KB'}
            </p>
          )}
        </div>
      </div>
    );
  }

  const navigationHint = getNavigationHint(item, isHebrew);
  const navigationIcon = getNavigationIcon(item);

  const getCardVariant = () => {
    if (item.source === 'ai_alert') return 'alert';
    if (item.source === 'clinical_win') return 'success';
    if (item.priority === 'critical' || item.priority === 'high') return 'warning';
    return 'default';
  };

  const variant = getCardVariant();

  const getSourceIcon = () => {
    if (item.iconEmoji) return item.iconEmoji;
    switch (item.source) {
      case 'ai_alert': return '\uD83E\uDD16';
      case 'dental_news': return '\uD83D\uDCF0';
      case 'practice_activity': return '\uD83C\uDFE5';
      case 'clinical_win': return '\uD83C\uDFC6';
      default: return '\uD83D\uDCCC';
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return isHebrew ? '\u05E2\u05DB\u05E9\u05D9\u05D5' : 'now';
    if (diffMins < 60) return isHebrew ? `${diffMins} \u05D3\u05E7\u05F3` : `${diffMins}m`;
    if (diffHours < 24) return isHebrew ? `${diffHours} \u05E9\u05E2\u05F3` : `${diffHours}h`;
    return isHebrew ? `${diffDays} \u05D9\u05DE\u05D9\u05DD` : `${diffDays}d`;
  };

  const title = isHebrew && item.titleHe ? item.titleHe : item.title;
  const summary = isHebrew && item.summaryHe ? item.summaryHe : item.summary;

  return (
    <article
      className={`feed-card feed-card-${variant} feed-card-${mode} ${isNew ? 'feed-card-new' : ''} ${navigationHint ? 'feed-card-clickable' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {item.source === 'ai_alert' && <div className="alert-border-indicator" />}

      <div className="feed-card-content">
        <div className="feed-card-header">
          <span className="source-icon">{getSourceIcon()}</span>
          {item.authorAvatar && (
            <img src={item.authorAvatar} alt="" className="author-avatar" />
          )}
          {item.author && <span className="author-name">{item.author}</span>}
          {item.sourceName && <span className="source-name">{item.sourceName}</span>}
          <span className="feed-card-time">{formatTime(item.timestamp)}</span>
        </div>

        <h4 className="feed-card-title">{title}</h4>

        {summary && mode !== 'minimal' && (
          <p className="feed-card-summary">{summary}</p>
        )}

        {item.imageUrl && mode === 'full' && (
          <div className="feed-card-image">
            <img src={item.imageUrl} alt="" loading="lazy" />
          </div>
        )}

        {item.source === 'ai_alert' && item.aiConfidence != null && (
          <div className="ai-alert-info">
            <span className="confidence-label">
              {isHebrew ? '\u05D1\u05D9\u05D8\u05D7\u05D5\u05DF:' : 'Confidence:'}
            </span>
            <span className="confidence-value">
              {Math.round(item.aiConfidence * 100)}%
            </span>
            {item.requiresAction && (
              <span className="requires-action-badge">
                {isHebrew ? '\u05D3\u05D5\u05E8\u05E9 \u05E4\u05E2\u05D5\u05DC\u05D4' : 'Action Required'}
              </span>
            )}
          </div>
        )}

        {item.source === 'clinical_win' && item.metric != null && (
          <div className="clinical-win-badge">
            <span className="win-metric">{item.metric}</span>
            <span className="win-label">{item.metricLabel}</span>
          </div>
        )}

        {navigationHint && (
          <div className="feed-card-nav-hint">
            <span className="nav-hint-icon">{navigationIcon}</span>
            <span className="nav-hint-text">{navigationHint}</span>
          </div>
        )}
      </div>

      <div className="feed-card-actions">
        <button
          className={`action-btn like-btn ${item.isLiked ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onLike?.(); }}
          title={isHebrew ? '\u05D0\u05D4\u05D1\u05EA\u05D9' : 'Like'}
          aria-label={isHebrew ? '\u05D0\u05D4\u05D1\u05EA\u05D9' : 'Like'}
        >
          {item.isLiked ? '\u2764\uFE0F' : '\uD83E\uDD0D'}
          {item.likeCount > 0 && <span className="action-count">{item.likeCount}</span>}
        </button>

        <button
          className={`action-btn bookmark-btn ${item.isBookmarked ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onBookmark?.(); }}
          title={isHebrew ? '\u05E9\u05DE\u05D5\u05E8' : 'Bookmark'}
          aria-label={isHebrew ? '\u05E9\u05DE\u05D5\u05E8' : 'Bookmark'}
        >
          {item.isBookmarked ? '\uD83D\uDD16' : '\uD83D\uDCD1'}
        </button>

        {item.externalUrl && (
          <button
            className="action-btn external-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
            }}
            title={isHebrew ? '\u05E4\u05EA\u05D7 \u05D1\u05D7\u05DC\u05D5\u05DF \u05D7\u05D3\u05E9' : 'Open in new tab'}
            aria-label={isHebrew ? '\u05E4\u05EA\u05D7 \u05D1\u05D7\u05DC\u05D5\u05DF \u05D7\u05D3\u05E9' : 'Open in new tab'}
          >
            \uD83D\uDD17
          </button>
        )}
      </div>
    </article>
  );
};
